<template>
  <div class="navbar-wrapper" :class="{ scrolled: isScrolled }">
    <nav class="navbar">
      <div class="navbar-container">
        <!-- 网站 Logo 区域 -->
        <router-link to="/" class="logo">
          <div class="logo-icon">
            <i class="iconfont icon-shipin" />
          </div>
          <span class="logo-text" v-if="settingInfo && settingInfo.site_name">{{ settingInfo.site_name }}</span>
        </router-link>

        <!-- 移动端菜单按钮 -->
        <button class="mobile-menu-btn" @click="toggleMobileMenu">
          <i :class="mobileMenuOpen ? 'el-icon-s-fold' : 'el-icon-s-unfold'" />
        </button>

        <!-- 桌面端导航链接 -->
        <div class="nav-links desktop-nav">
          <router-link v-for="item in list" :key="item.path" :to="item.path" :class="{ 'active': isActive(item.path) }"
            @click="closeMobileMenu"
            :style="{ display: (item.isLogin && !isLoggedIn) ? 'none' : '', color: item.isLogin ? 'red' : '' }">
            {{ $t(item.name) }}
          </router-link>
        </div>

        <!-- 移动端侧边栏导航（支持多级菜单） -->
        <div class="mobile-nav" :class="{ 'mobile-open': mobileMenuOpen }">
          <!-- 移动端用户信息头部 -->
          <div class="mobile-nav-header">
            <div v-if="isLoggedIn" class="mobile-user-info">
              <div class="mobile-avatar">
                <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="avatar">
                <i v-else class="el-icon-user-solid" />
              </div>
              <div class="mobile-user-text">
                <div class="mobile-user-name">
                  {{ userInfo.nickname || $t('TopNav.创作者') }}
                </div>
                <div class="mobile-user-email">
                  {{ userInfo.email }}
                </div>
              </div>
            </div>
            <div v-else class="mobile-user-info">
              <div class="mobile-avatar">
                <i class="el-icon-user-solid" />
              </div>
              <div class="mobile-user-text">
                <div class="mobile-user-name">
                  {{ $t('TopNav.游客') }}
                </div>
                <div class="mobile-user-email">
                  {{ $t('TopNav.请登录后体验完整功能') }}
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端导航菜单列表（支持多级展开） -->
          <div class="mobile-nav-list">
            <div v-for="item in list" :key="item.path" class="mobile-nav-item"
              :style="{ display: (item.isLogin && !isLoggedIn) ? 'none' : '' }">
              <div class="mobile-nav-link" @click="handleMobileNavClick(item)">
                <div class="nav-link-left">
                  <i :class="item.icon" />
                  <span>{{ $t(item.name) }}</span>
                </div>
                <!-- 如果有子菜单，显示展开/收起的箭头图标 -->
                <i v-if="item.child && item.child.length"
                  :class="expandedMenus[item.path] ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" class="nav-arrow" />
              </div>
              <!-- 子菜单列表，通过 expandedMenus 控制展开状态 -->
              <div v-if="item.child && item.child.length" class="mobile-nav-child"
                :class="{ expanded: expandedMenus[item.path] }">
                <div v-for="child in item.child" :key="child.path" class="mobile-nav-child-link"
                  @click="handleMobileChildClick(child.path)">
                  <i :class="child.icon" />
                  <span>{{ $t(child.name) }}</span>
                  <!-- 可选的角标，如消息数量等 -->
                  <span v-if="child.badge" class="badge">{{ child.badge }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端底部按钮区域（登录/退出） -->
          <div class="mobile-nav-footer">
            <template v-if="!isLoggedIn">
              <router-link to="/login" class="mobile-btn mobile-btn-ghost" @click="closeMobileMenu">
                {{ $t('TopNav.登录') }}
              </router-link>
              <router-link to="/register" class="mobile-btn mobile-btn-primary" @click="closeMobileMenu">
                {{ $t('TopNav.免费试用') }}
              </router-link>
            </template>
            <template v-else>
              <div class="mobile-logout" @click="handleLogout">
                <i class="el-icon-switch-button" />
                <span>{{ $t('TopNav.退出登录') }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- 桌面端右侧操作区域 -->
        <div class="nav-actions desktop-actions">
          <!-- 语言切换组件 -->
          <LangSwitcher />

          <!-- 用户未登录时显示登录/注册按钮 -->
          <template v-if="!isLoggedIn">
            <router-link to="/login" class="btn btn-ghost" @click="closeMobileMenu">
              {{ $t('TopNav.登录') }}
            </router-link>
            <router-link to="/register" class="btn btn-primary" @click="closeMobileMenu">
              {{ $t('TopNav.免费试用') }}
            </router-link>
          </template>

          <!-- 用户已登录时显示用户头像下拉菜单 -->
          <template v-else>
            <div class="user-dropdown" @click="toggleUserMenu">
              <div class="user-avatar">
                <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="avatar">
                <i v-else class="el-icon-user-solid" />
              </div>
              <i class="el-icon-arrow-down" :class="{ rotated: userMenuOpen }" />
            </div>
            <!-- 用户下拉菜单内容 -->
            <div v-show="userMenuOpen" class="user-menu-dropdown" @click.stop>
              <div class="user-menu-header">
                <div class="user-menu-avatar">
                  <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="avatar">
                  <i v-else class="el-icon-user-solid" />
                </div>
                <div class="user-menu-info">
                  <div class="user-menu-name">
                    {{ userInfo.nickname || $t('TopNav.创作者') }}
                  </div>
                  <div class="user-menu-email">
                    {{ userInfo.email }}
                  </div>
                </div>
              </div>
              <div class="user-menu-divider" />
              <router-link to="/my" class="user-menu-item" @click="closeAllMenus">
                <i class="el-icon-data-line" />
                <span>{{ $t('TopNav.个人中心') }}</span>
              </router-link>
              <router-link to="/my/my-video" class="user-menu-item" @click="closeAllMenus">
                <i class="el-icon-video-camera" />
                <span>{{ $t('TopNav.我的视频') }}</span>
              </router-link>
              <router-link to="/my/credit" class="user-menu-item" @click="closeAllMenus">
                <i class="el-icon-coin" />
                <span>{{ $t('TopNav.额度管理') }}</span>
              </router-link>
              <div class="user-menu-divider" />
              <div class="user-menu-item logout" @click="handleLogout">
                <i class="el-icon-switch-button" />
                <span>{{ $t('TopNav.退出登录') }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </nav>

    <!-- 移动端菜单打开时的遮罩层 -->
    <transition name="fade">
      <div v-if="mobileMenuOpen" class="mobile-overlay" @click="closeMobileMenu" />
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'TopNav',
  data() {
    return {
      // 导航菜单配置：支持一级菜单和二级子菜单（child）
      list: [
        // { path: '/', name: 'TopNav.首页', icon: 'el-icon-house' },
        // {
        //   path: '/ai',
        //   name: 'TopNav.AI创作',
        //   icon: 'el-icon-magic-stick',
        //   child: [
        //     { path: '/ai/reference', name: 'TopNav.多参考', icon: 'el-icon-film' },
        //     { path: '/ai', name: 'TopNav.文本视频', icon: 'el-icon-edit-outline' },
        //     { path: '/ai/image-to-video', name: 'TopNav.图片视频', icon: 'el-icon-picture-outline' },
        //     { path: '/ai/text-to-image', name: 'TopNav.文生图', icon: 'el-icon-brush' },
        //     { path: '/ai/image-to-image', name: 'TopNav.图生图', icon: 'el-icon-camera' }
        //   ]
        // },
        {
          path: '/ai',
          name: 'HomeLayout.AI视频',
          icon: 'el-icon-magic-stick',
          child: [
            { path: '/ai/reference', name: 'TopNav.多参考', icon: 'el-icon-film' },
            { path: '/ai', name: 'TopNav.文本视频', icon: 'el-icon-edit-outline' },
            { path: '/ai/image-to-video', name: 'TopNav.图片视频', icon: 'el-icon-picture-outline' }
          ]
        },
        {
          path: '/ai/text-to-image',
          name: 'Home.AI图片',
          icon: 'el-icon-picture-outline',
          child: [
            { path: '/ai/text-to-image', name: 'TopNav.文生图', icon: 'el-icon-brush' },
            { path: '/ai/image-to-image', name: 'TopNav.图生图', icon: 'el-icon-camera' }
          ]
        },
        { path: '/video-show', name: 'TopNav.视频展示', icon: 'el-icon-video-play' },
        { path: '/price', name: 'TopNav.价格', icon: 'el-icon-thumb' },
        { path: '/news', name: 'TopNav.新闻资讯', icon: 'el-icon-reading' },
        {
          path: '/my',
          name: 'TopNav.会员中心',
          icon: 'el-icon-trophy-1',
          child: [
            { path: '/my', name: 'TopNav.概览', icon: 'el-icon-data-line' },
            { path: '/my/my-video', name: 'TopNav.我的创作', icon: 'el-icon-folder-opened' },
            { path: '/my/membership', name: 'TopNav.会员中心', icon: 'iconfont icon-huangguan' },
            { path: '/my/credit', name: 'TopNav.额度管理', icon: 'el-icon-coin' },
            { path: '/my/billing', name: 'TopNav.账单管理', icon: 'el-icon-document-copy' },
            { path: '/my/task', name: 'TopNav.生成任务', icon: 'el-icon-more-outline' }
          ],
          isLogin: true
        },
      ],
      // 记录移动端各个菜单的展开状态（key 为菜单路径）
      expandedMenus: {},
      // 移动端侧边栏是否打开
      mobileMenuOpen: false,
      // 桌面端用户下拉菜单是否打开
      userMenuOpen: false,
      // 页面滚动状态（用于改变导航栏样式）
      isScrolled: false,
    }
  },
  computed: {
    ...mapState(['userInfo', 'settingInfo']),
    isLoggedIn() {
      return !!this.userInfo && !!this.userInfo.email
    }
  },
  mounted() {
    // 监听页面滚动事件，用于改变导航栏样式
    window.addEventListener('scroll', this.handleScroll)
    // 监听全局点击事件，用于关闭用户下拉菜单
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    // 组件销毁前移除事件监听，防止内存泄漏
    window.removeEventListener('scroll', this.handleScroll)
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    // 判断当前路由是否与菜单项匹配（用于高亮显示）
    isActive(path) {
      const currentPath = this.$route.path
      if (path === '/') {
        return currentPath === '/'
      }
      if (path === '/ai') {
        return currentPath === '/ai' || currentPath.startsWith('/ai/')
      }
      if (path === '/my') {
        return currentPath === '/my' || currentPath.startsWith('/my/')
      }
      if (path === '/news') {
        return currentPath === '/news' || currentPath.startsWith('/news/')
      }
      return currentPath === path
    },
    //  切换移动端菜单的打开/关闭状态
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
      if (this.mobileMenuOpen) {
        document.body.style.overflow = 'hidden'
        this.expandedMenus = {}
      } else {
        document.body.style.overflow = ''
      }
    },
    // 关闭移动端菜单
    closeMobileMenu() {
      this.mobileMenuOpen = false
      document.body.style.overflow = ''
      this.expandedMenus = {}
    },
    // 切换桌面端用户下拉菜单
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen
    },
    // 关闭桌面端用户下拉菜单
    closeUserMenu() {
      this.userMenuOpen = false
    },
    // 关闭所有菜单（移动端菜单、用户下拉菜单）
    closeAllMenus() {
      this.closeMobileMenu()
      this.closeUserMenu()
    },
    // 全局点击处理：如果点击发生在用户下拉菜单外部，则关闭菜单
    handleClickOutside(event) {
      const userDropdown = this.$el.querySelector('.user-dropdown')
      const userMenu = this.$el.querySelector('.user-menu-dropdown')
      if (userDropdown && !userDropdown.contains(event.target) && userMenu && !userMenu.contains(event.target)) {
        this.userMenuOpen = false
      }
    },
    //  页面滚动处理：改变 isScrolled 状态，用于导航栏的样式变化（如背景透明/不透明）
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    // 用户退出登录
    handleLogout() {
      this.$confirm(this.$t('TopNav.确定要退出登录吗？'), this.$t('TopNav.提示'), {
        confirmButtonText: this.$t('TopNav.确定'),
        cancelButtonText: this.$t('TopNav.取消'),
        type: 'warning'
      }).then(() => {
        this.$store.dispatch("logout").then(() => {
          this.userMenuOpen = false
          this.$message.success(this.$t('TopNav.退出成功'))
          if (this.$route.path.startsWith('/my')) {
            this.$router.push('/')
          }
          this.closeMobileMenu()
        }).catch(() => { })
      }).catch(() => { })
    },
    // 移动端点击一级菜单项的处理
    handleMobileNavClick(item) {
      if (item.child && item.child.length) {
        const currentState = this.expandedMenus[item.path]
        this.expandedMenus = {}
        if (!currentState) {
          this.$set(this.expandedMenus, item.path, true)
        }
      } else {
        if (this.$route.path !== item.path) {
          this.$router.push(item.path)
        }
        this.closeMobileMenu()
      }
    },
    // 移动端点击子菜单项的处理：跳转路由并关闭移动端菜单
    handleMobileChildClick(path) {
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
      this.closeMobileMenu()
    }
  }
}
</script>

<style lang="scss" scoped>
// 导航栏外层容器，固定定位并控制滚动时的样式变化
.navbar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;

  // 页面滚动超过 10px 时的样式（背景更实，阴影加深）
  &.scrolled {
    .navbar {
      padding: 0.75rem 2rem;
      background: rgba(15, 15, 26, 0.98);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    }
  }

  .navbar {
    padding: 1rem 2rem;
    background: rgba(15, 15, 26, 0.95);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    transition: all 0.3s ease;

    .navbar-container {
      max-width: 1200px;
      margin: 0 auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;

      // Logo 样式
      .logo {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        text-decoration: none;
        transition: opacity 0.3s;

        &:hover {
          opacity: 0.9;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #6366f1, #a855f7);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          color: white;
        }

        .logo-text {
          font-size: 1.5rem;
          font-weight: 800;
          background: linear-gradient(135deg, #6366f1, #a855f7);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      // 移动端菜单按钮，默认隐藏，仅在移动端显示
      .mobile-menu-btn {
        display: none;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        width: 40px;
        height: 40px;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #f1f5f9;
        font-size: 1.25rem;
        transition: all 0.3s;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: #6366f1;
        }
      }

      // 桌面端导航链接容器
      .desktop-nav {
        display: flex;
        gap: 2.5rem;

        a {
          color: #ffffff;
          text-decoration: none;
          font-size: 1.0625rem;
          font-weight: 500;
          transition: all 0.3s;
          letter-spacing: 0.5px;
          position: relative;

          // 下划线动画
          &::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 2px;
            background: linear-gradient(135deg, #6366f1, #a855f7);
            transition: width 0.3s;
          }

          &:hover::after,
          &.active::after {
            width: 100%;
          }

          &:hover {
            color: #818cf8;
          }

          &.active {
            color: #818cf8;
          }
        }
      }

      // 桌面端右侧操作区域（按钮、语言切换、用户菜单）
      .desktop-actions {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .btn {
          padding: 0.625rem 1.25rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          text-decoration: none;

          &.btn-ghost {
            background: transparent;
            color: #f1f5f9;

            &:hover {
              background: rgba(255, 255, 255, 0.05);
              transform: translateY(-2px);
            }
          }

          &.btn-primary {
            background: linear-gradient(135deg, #6366f1, #a855f7);
            color: white;

            &:hover {
              opacity: 0.9;
              transform: translateY(-2px);
            }
          }
        }

        // 用户头像下拉触发器
        .user-dropdown {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 20px;
          transition: all 0.3s;

          &:hover {
            background: rgba(255, 255, 255, 0.05);
          }

          i {
            color: #94a3b8;
            font-size: 0.75rem;
            transition: transform 0.3s;

            &.rotated {
              transform: rotate(180deg);
            }
          }

          .user-avatar {
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1, #a855f7);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            overflow: hidden;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            i {
              font-size: 1rem;
              color: white;
            }
          }
        }

        // 用户下拉菜单内容
        .user-menu-dropdown {
          position: absolute;
          top: calc(100% + 0.5rem);
          right: 0;
          width: 260px;
          background: rgba(15, 15, 26, 0.98);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 0.75rem;
          z-index: 1001;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);

          .user-menu-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem;

            .user-menu-avatar {
              width: 48px;
              height: 48px;
              background: linear-gradient(135deg, #6366f1, #a855f7);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              overflow: hidden;

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }

              i {
                font-size: 1.5rem;
              }
            }

            .user-menu-info {
              flex: 1;

              .user-menu-name {
                font-weight: 600;
                color: #f1f5f9;
                margin-bottom: 0.25rem;
              }

              .user-menu-email {
                font-size: 0.75rem;
                color: #94a3b8;
              }
            }
          }

          .user-menu-divider {
            height: 1px;
            background: rgba(255, 255, 255, 0.08);
            margin: 0.5rem 0;
          }

          .user-menu-item {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem;
            border-radius: 8px;
            color: #94a3b8;
            text-decoration: none;
            transition: all 0.3s;
            cursor: pointer;

            i {
              width: 20px;
              font-size: 1rem;
            }

            &:hover {
              background: rgba(99, 102, 241, 0.1);
              color: #f1f5f9;
            }

            &.logout:hover {
              background: rgba(239, 68, 68, 0.1);
              color: #ef4444;
            }
          }
        }
      }

      // 移动端侧边栏导航样式（默认从右侧滑入）
      .mobile-nav {
        position: fixed;
        top: 0;
        right: -100%;
        width: 80%;
        max-width: 320px;
        height: 100vh;
        background: #0f0f1a;
        display: flex;
        flex-direction: column;
        transition: right 0.3s ease;
        z-index: 1001;
        box-shadow: -5px 0 30px rgba(0, 0, 0, 0.3);
        overflow-y: auto;

        &.mobile-open {
          right: 0;
        }

        .mobile-nav-header {
          padding: 1.5rem;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.05));
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);

          .mobile-user-info {
            display: flex;
            align-items: center;
            gap: 1rem;

            .mobile-avatar {
              width: 48px;
              height: 48px;
              background: linear-gradient(135deg, #6366f1, #a855f7);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              overflow: hidden;

              i {
                font-size: 1.5rem;
              }

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }

            .mobile-user-text {
              flex: 1;

              .mobile-user-name {
                font-weight: 600;
                color: #f1f5f9;
                margin-bottom: 0.25rem;
              }

              .mobile-user-email {
                font-size: 0.7rem;
                color: #94a3b8;
              }
            }
          }
        }

        .mobile-nav-list {
          flex: 1;
          padding: 0.5rem 0;

          .mobile-nav-item {
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);

            .mobile-nav-link {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 1rem 1.5rem;
              cursor: pointer;
              transition: background 0.3s;

              &:hover {
                background: rgba(255, 255, 255, 0.05);
              }

              .nav-link-left {
                display: flex;
                align-items: center;
                gap: 0.75rem;

                i {
                  font-size: 1.125rem;
                  color: #818cf8;
                  width: 24px;
                }

                span {
                  color: #e2e8f0;
                  font-size: 0.9375rem;
                }
              }

              .nav-arrow {
                color: #64748b;
                font-size: 0.875rem;
                transition: transform 0.3s;
              }
            }

            // 子菜单容器，通过 max-height 控制展开/收起的动画
            .mobile-nav-child {
              max-height: 0;
              overflow: hidden;
              transition: max-height 0.3s ease;
              background: rgba(0, 0, 0, 0.2);

              &.expanded {
                max-height: 500px;
              }

              .mobile-nav-child-link {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.875rem 1.5rem 0.875rem 3rem;
                cursor: pointer;
                transition: background 0.3s;
                position: relative;

                &:hover {
                  background: rgba(255, 255, 255, 0.03);
                }

                i {
                  font-size: 1rem;
                  color: #94a3b8;
                  width: 20px;
                }

                span {
                  color: #94a3b8;
                  font-size: 0.875rem;
                }

                .badge {
                  margin-left: auto;
                  padding: 0.125rem 0.5rem;
                  background: rgba(99, 102, 241, 0.2);
                  border-radius: 20px;
                  font-size: 0.7rem;
                  color: #818cf8;
                }
              }
            }
          }
        }

        // 移动端底部按钮区域
        .mobile-nav-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          display: flex;
          gap: 0.75rem;

          .mobile-btn {
            flex: 1;
            padding: 0.625rem;
            border-radius: 10px;
            font-weight: 600;
            font-size: 0.875rem;
            text-align: center;
            text-decoration: none;
            transition: all 0.3s;

            &.mobile-btn-ghost {
              background: transparent;
              border: 1px solid rgba(255, 255, 255, 0.2);
              color: #f1f5f9;

              &:hover {
                background: rgba(255, 255, 255, 0.05);
              }
            }

            &.mobile-btn-primary {
              background: linear-gradient(135deg, #6366f1, #a855f7);
              color: white;

              &:hover {
                opacity: 0.9;
              }
            }
          }

          .mobile-logout {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            padding: 0.625rem;
            border-radius: 10px;
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid rgba(239, 68, 68, 0.2);
            color: #ef4444;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
              background: rgba(239, 68, 68, 0.2);
            }
          }
        }
      }
    }
  }

  // 移动端遮罩层，点击关闭菜单
  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 320px;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
}

// 淡入淡出动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// 语言切换器样式（深度作用，因为 LangSwitcher 可能是一个独立组件）
.nav-actions {
  :deep(.lang-switcher) {
    position: relative;

    .lang-btn {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.625rem;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: #94a3b8;
      font-size: 0.8125rem;
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        border-color: rgba(99, 102, 241, 0.5);
        color: #f1f5f9;
      }

      i {
        font-size: 0.625rem;
        transition: transform 0.3s;
      }
    }

    .lang-dropdown {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      min-width: 150px;
      max-height: 300px;
      overflow-y: auto;
      background: rgba(15, 15, 26, 0.98);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      padding: 0.375rem;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-10px);
      transition: all 0.3s;
      z-index: 1001;
      backdrop-filter: blur(20px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.2);
        border-radius: 3px;
      }
    }

    &.active .lang-dropdown {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    .lang-option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.625rem;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 0.8125rem;
      color: #94a3b8;

      &:hover {
        background: rgba(99, 102, 241, 0.15);
        color: #f1f5f9;
      }

      &.active {
        background: linear-gradient(135deg, #6366f1, #a855f7);
        color: white;
      }

      .lang-flag {
        font-size: 0.875rem;
      }
    }
  }
}

// 响应式断点：1024px 以下隐藏 Logo 文字（仅保留图标）
@media (max-width: 1024px) {
  .navbar-wrapper .navbar .navbar-container .logo .logo-text {
    display: none;
  }
}

// 响应式断点：768px 以下显示移动端菜单按钮，隐藏桌面端导航和操作区
@media (max-width: 768px) {
  .navbar-wrapper {
    &.scrolled .navbar {
      padding: 0.75rem 1rem;
    }

    .navbar {
      padding: 0.75rem 1rem;

      .navbar-container {
        .logo {
          display: flex;

          .logo-text {
            display: none;
          }
        }

        .mobile-menu-btn {
          display: flex;
          z-index: 1002;
        }

        .desktop-nav,
        .desktop-actions {
          display: none;
        }
      }
    }
  }
}

// 极端小屏下调整遮罩层右侧距离
@media (max-width: 400px) {
  .mobile-overlay {
    right: 80% !important;
  }
}
</style>